import elementResizeDetectorMaker, { ErdmOptions } from 'element-resize-detector';
import memoizeOne from 'memoize-one';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { usePrevious, useUpdate } from 'react-use';

type ResizeDetectorCallback<T, R> = (el: T) => R;
type Erd = ReturnType<typeof elementResizeDetectorMaker>;

const defaultOptions = { strategy: 'scroll' } as ErdmOptions;

const isClient = () => typeof window !== 'undefined';

export function useResizeDetector<T extends HTMLElement, R>(
    ref: RefObject<T>,
    callback: ResizeDetectorCallback<T, R>
): [R?] {
    const [result, setResult] = useState<R>();
    const [erd] = useState(isClient() ? () => elementResizeDetectorMaker(defaultOptions) : ({} as Erd));

    useEffect(() => {
        const node = ref.current;

        if (node) {
            setResult(callback(node));
            erd.listenTo(node, (el: T) => setResult(callback(el)));
            return () => erd.uninstall(node);
        }

        return undefined;
    }, [erd, callback, ref?.current]);

    return [result];
}

// todo: упростить... или удалить
export function useResizeDetectorMany<T extends HTMLElement, R>(
    refs: RefObject<T>[],
    callback: ResizeDetectorCallback<T, R>
): [R[]?] {
    const [results, setResults] = useState<R[]>();
    const [erd] = useState(isClient() ? () => elementResizeDetectorMaker(defaultOptions) : ({} as Erd));

    const update = useCallback(
        (index: number, result: R) => {
            setResults((prevResults) => {
                const newResults = prevResults ? prevResults.slice(0, refs.length) : [];
                newResults[index] = result;
                return newResults;
            });
        },
        [refs.length]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoNodes = useCallback(
        memoizeOne((...nodes: (T | null)[]) => nodes),
        []
    );
    const nodes = useRef<(T | null)[]>(memoNodes(...refs.map(({ current }) => current)));
    useEffect(() => {
        nodes.current = memoNodes(...refs.map(({ current }) => current));
    });

    const prevNodes = usePrevious(nodes.current);
    const rerender = useUpdate();
    useEffect(() => {
        if (nodes.current !== prevNodes) {
            rerender();
        }
    });

    const allNodes = nodes.current;
    useEffect(() => {
        allNodes.forEach((node, index) => {
            if (node) {
                update(index, callback(node));
                erd.listenTo(node, (el) => update(index, callback(el as T)));
            }
        });
        return () =>
            allNodes.forEach((node) => {
                if (node) {
                    erd.uninstall(node);
                }
            });
    }, [erd, callback, allNodes, update]);

    return [results];
}
